/**
 * TikTok Embed Extractor
 *
 * 목적:
 * - TikTok 공식 embed HTML(blockquote + script) 또는 생 URL에서
 *   영상 URL을 추출/정규화하여 URL-only 저장을 가능하게 한다.
 *
 * 주의:
 * - 이 모듈은 "표시용 embed 렌더"가 아니라, URL 추出/정규화 전용이다.
 */

/** Decode HTML entities in pasted embed text. */
function decodeHtmlEntities(text: string): string {
    try {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = String(text || "");
        return textarea.value;
    } catch {
        return String(text || "");
    }
}

/**
 * Extract a TikTok video URL from official embed HTML or any pasted text.
 * Prefers cite="..." attribute.
 */
function extractTikTokVideoUrlFromOfficialEmbedHtml(htmlOrText: string): string | null {
    const decoded = decodeHtmlEntities(String(htmlOrText || ""));
    if (!decoded) {
        return null;
    }

    const cite = decoded.match(/\bcite\s*=\s*["']([^"']+)["']/i);
    if (cite && cite[1]) {
        return cite[1].trim();
    }

    const urlMatch = decoded.match(
        /https?:\/\/(?:www\.)?tiktok\.com\/@[^\s"'<>]+\/video\/\d+(?:(?:\?[^\s"'<>#]*)?(?:#[^\s"'<>]*)?)?/i,
    );
    if (urlMatch && urlMatch[0]) {
        return urlMatch[0].trim();
    }

    return null;
}

/**
 * Normalize and validate TikTok video URL.
 * Accepts only https://www.tiktok.com/@.../video/{id}
 */
function normalizeTikTokVideoUrl(input: string): string | null {
    const raw = String(input || "").trim();
    if (!raw) {
        return null;
    }

    const match = raw.match(
        /^(?:https?:\/\/)?(?:www\.|m\.)?tiktok\.com\/@[^\s"'<>]+\/video\/\d+(?:(?:\?[^\s"'<>#]*)?(?:#[^\s"'<>]*)?)?/i,
    );
    if (!match) {
        return null;
    }

    let urlText = match[0];
    if (!/^https?:\/\//i.test(urlText)) {
        urlText = `https://${urlText}`;
    }
    urlText = urlText.replace(/^http:\/\//i, "https://").replace(/[)\],.。]+$/u, "");

    try {
        const parsedUrl = new URL(urlText);
        parsedUrl.protocol = "https:";
        parsedUrl.hostname = "www.tiktok.com";
        parsedUrl.hash = "";
        parsedUrl.search = "";
        urlText = parsedUrl.toString();
    } catch {
        // keep best-effort
    }

    urlText = urlText.replace(/\/$/, "");
    return urlText;
}

export {
    extractTikTokVideoUrlFromOfficialEmbedHtml,
    normalizeTikTokVideoUrl,
    decodeHtmlEntities as decodeTikTokEmbedHtmlEntities,
};
